import { getMasterData } from 'master-data'
import type { Channel, Device } from 'types/masterData'
import { getHeaderAkamai } from 'utils/akamai'
import { env } from 'utils/envManager'

import { MENU_SERVICE_URL } from '../../constants'

export type MenuType = {
	id: string
	subBrandId?: string
	label: string
	labelId: string
	subLabel?: string
	subLabelId?: string
	link?: string
	url?: string
	legacyUrl?: string
	menus?: MenuType[]
	type?: MenuSectionType
	analyticLabel: string
	image?: MenuImage
	images?: MenuImageDevice
	css?: string[]
	style?: string[]
	hideIn?: HideIn
}

export enum MenuSectionType {
	Brand = 'BRAND',
	Multibrand = 'MULTIBRAND',
	Subbrand = 'SUBBRAND',
	Colecciones = 'colecciones',
	Collections = 'COLLECTIONS',
	Destacados = 'destacados',
	Featured = 'FEATURED',
	Group = 'GROUP',
}

export enum MenuLinksType {
	BRAND = 'BRAND',
	MULTIBRAND = 'MULTIBRAND',
	EDIT = 'EDIT',
	CATALOG = 'CATALOG',
}

interface HideIn {
	desktop: boolean
	mobile: boolean
}

export interface MenuImage {
	alt: string
	link: string
	src: string
	fontColor: string
	analitycLabel: string
}

interface MenuImageDevice {
	mobile?: MenuImage
	desktop?: MenuImage
}

export interface GetMenuProps {
	country: string
	language: string
	device: Device
	customDate: Date | null
	isNewServiceEnable: boolean
}

interface GetMenuUrlProps {
	country: string
	language: string
	device: Device
	isClient: boolean
	customDate: Date | null
	isNewServiceEnable: boolean
}

interface GetMenuUrlServerClientProps {
	country: string
	language: string
	device: Device
	isNewServiceEnable: boolean
}

export interface MenuService {
	menus: MenuType[]
	shop: Channel
	version: string
}

const NUMBER_TWO = 2

function formatDate(date: Date) {
	const datePart = [date.getDate(), date.getMonth() + 1, date.getFullYear()]
		.map((n) => n.toString().padStart(NUMBER_TWO, '0'))
		.join('')
	const timePart = [date.getHours(), date.getMinutes(), date.getSeconds()]
		.map((n) => n.toString().padStart(NUMBER_TWO, '0'))
		.join('')
	return `${datePart}T${timePart}`
}

export function getMenuUrl({
	country,
	language,
	device,
	isClient,
	customDate,
	isNewServiceEnable,
}: GetMenuUrlProps): string {
	let url

	if (isClient) {
		url = getMenuUrlClient({ country, language, device, isNewServiceEnable })
	} else {
		url = getMenuUrlServer({ country, language, device, isNewServiceEnable })
	}

	if (customDate) {
		url.searchParams.append('configDate', formatDate(customDate))
	}

	return url.href
}

export function getMenuUrlClient({
	country,
	language,
	device,
	isNewServiceEnable,
}: GetMenuUrlServerClientProps): URL {
	const legacyService = `${process.env.NEXT_PUBLIC_LEGACY_MENU_SERVICE}${
		env.NEXT_PUBLIC_CHANNEL
	}/${device}/${country.toUpperCase()}/${language}`
	const newService = `${env.NEXT_PUBLIC_SITE_BASE_URL}${MENU_SERVICE_URL}/v1/menu/${
		env.NEXT_PUBLIC_CHANNEL
	}/${country.toUpperCase()}/${language}`

	return new URL(isNewServiceEnable ? newService : legacyService)
}

export function getMenuUrlServer({
	country,
	language,
	device,
	isNewServiceEnable,
}: GetMenuUrlServerClientProps): URL {
	const legacyService = `${process.env.NEXT_PUBLIC_LEGACY_MENU_SERVICE}${
		env.NEXT_PUBLIC_CHANNEL
	}/${device}/${country.toUpperCase()}/${language}`
	const newService = `${env.MENU_SERVICE}/v1/menu/${
		env.NEXT_PUBLIC_CHANNEL
	}/${country.toUpperCase()}/${language}`

	return new URL(isNewServiceEnable ? newService : legacyService)
}

export const getMenu = async (
	isNewServiceEnable: boolean
): Promise<MenuService> => {
	const {
		device,
		customDate,
		country: { countryISO: country, languageISO: language },
	} = getMasterData()
	const url = getMenuUrl({
		country,
		language,
		device,
		customDate,
		isNewServiceEnable,
		isClient: false,
	})

	let headers = {}
	const akamaiHeader = await getHeaderAkamai()

	if (!(akamaiHeader instanceof Error) && akamaiHeader) {
		const PREFIX = 'genesis'
		headers = {
			[akamaiHeader.name]: `${akamaiHeader.value}-${PREFIX}`,
		}
	}

	const response = await fetch(url, {
		headers,
		next: {
			revalidate: 0,
		},
	})

	if (!response.ok) {
		throw new Error(
			`Error Fetching Menu { url: ${url}, status: ${response.status}} }`
		)
	}
	return response.json()
}

export const getClientMenu = async (
	props: GetMenuProps
): Promise<MenuService> => {
	const url = getMenuUrl({
		...props,
		isClient: true,
	})

	const response = await fetch(url, {
		next: {
			revalidate: 0,
		},
	})

	if (!response.ok) {
		throw new Error(
			`Error Fetching Menu { status: ${
				response.status
			}, body: ${await response.text()} }`
		)
	}
	return response.json()
}
